import { useEffect, useRef } from 'react';
import {
  addDoc,
  collection,
  onSnapshot
} from 'firebase/firestore';
import db from '@services/firebase-service';
import { type InteractionType } from '@components/Presentation/types';
import { logsToAwsCloudWatch } from '@services/logger.service';
import { generateResponse } from '@services/generative-ai';
import { shuffle } from 'lodash';

const generateAlphabet = (number: number) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return alphabet[number] || '';
};

interface Feature {
  title: string;
  type: InteractionType;
  options: {
    option_number: number;
    body: string;
  }[];
  setting: {
    prefix: string;
    OptionsList: 'character' | 'number';
    optionOrderType: 'number' | 'letter';
  };
}

interface UseGeneratedResponsesProps {
  feature: Feature;
  streamTitle: string;
  streamId: string;
  enabled?: boolean;
  onComment?: (comment: OnCommentProps) => void;
}

interface OnCommentProps {
  message: string;
  user: string;
  interaction: InteractionType;
  streamId: string;
}
const handleComment = async ({ message, user, interaction, streamId }: OnCommentProps) => {
  if (!message || !user || !interaction || !streamId) return;

  const rawMessagesRef = collection(db, 'streams', streamId, 'rawMessages');

  const document = {
    upvotes: 0,
    message,
    time: Date.now(),
    userId: user,
    name: user,
    interaction
  };

  try {
    await addDoc(rawMessagesRef, document);
  } catch (error: any) {
    logsToAwsCloudWatch(error?.message);
  }
};

const getOptions = (feature: Feature) => {
  if (!feature || !feature.options) return '';

  const optionBodies = feature.options.map(option => option.body);
  let options: string[] = [...optionBodies];

  if (['pollWithOptions', 'freeForm'].includes(feature.type)) {
    const listByNumber = feature.setting.OptionsList === 'number';
    const extras = feature.options.map(option =>
      listByNumber
        ? option.option_number.toString()
        : generateAlphabet(option.option_number - 1)
    );
    options.push(...extras);
  }

  if (feature.type === 'multipleChoice') {
    options = [];
    const orderByNumber = feature.setting.optionOrderType === 'number';
    const extras = feature.options.map(option =>
      `${feature.setting.prefix}${orderByNumber
        ? option.option_number
        : generateAlphabet(option.option_number - 1)
      }`
    );
    options.push(...extras);
  }

  if (feature.type === 'quiz') {
    const optionOrderType = feature.setting.optionOrderType ?? 'number';
    const orderByNumber = optionOrderType === 'number';
    options = [];
    const extras = feature.options.map(option =>
      `${orderByNumber ? option.option_number : generateAlphabet(option.option_number - 1)}`
    );
    options.push(...extras);
  }

  return shuffle(options).join(',');
};

export const useGeneratedResponses = ({
  feature,
  streamId,
  enabled = true,
  streamTitle,
  onComment = handleComment
}: UseGeneratedResponsesProps) => {
  useEffect(() => {
    if (!enabled) return;
    if (!feature) return;

    if ([
      'PDF Slide',
      'wheel',
      'newWheel',
      'fantasticFans',
      'credits',
      'textTrack'
    ].includes(feature.type)) return;

    const options = getOptions(feature);

    const title = feature.type === 'Quick Questions' ? streamTitle : feature.title;

    generateResponse({
      topic: streamTitle,
      question: title,
      interaction: feature.type,
      options,
      streamId
    }).catch((err) => {
      console.error(err);
    });
  }, [feature?.type, enabled]);

  const queue = useRef<Comment[]>([]);

  useEffect(() => {
    if (!enabled) return;
    if (!streamId) return;

    const collectionRef = collection(db, 'streams', streamId, 'generatedMessages');

    const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
      for (const change of snapshot.docChanges()) {
        if (change.type === 'added') {
          const data: any = change.doc.data();

          queue.current.push({
            id: change.doc.id,
            ...data
          });
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [streamId, enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (!feature) return;

    if ([
      'PDF Slide',
      'wheel',
      'newWheel',
    ].includes(feature.type)) return;

    const interval = setInterval(() => {
      const filteredQueue = queue.current.filter(item => item.interaction === feature.type);
      const item = filteredQueue.shift();
      queue.current = filteredQueue;

      if (!item || item.interaction !== feature.type) return;

      onComment({
        streamId,
        message: item.message,
        user: item.userId,
        interaction: item.interaction as InteractionType
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [feature?.type, enabled]);
};

interface Comment {
  id: string;
  interaction: string;
  message: string;
  name: string;
  time: number;
  userId: string;
  upvotes: number;
}
